import React from "react";
import { TouchableOpacity } from "react-native";
import styled from "styled-components/native";
import { Spacer, VDBText } from "..";
import { SpaceType } from "../Spacer/types";
import { useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useCopilot, TooltipProps } from "react-native-copilot";
import { useDispatch } from "react-redux";
import { setCoachOverlayToast } from "@redux/slices/appStateSlice";
import { useSelector } from "react-redux";
import { useOverlayConfig } from "@redux/slices/globalSlice";
import { useTranslation } from "react-i18next";
import { css } from "styled-components/native";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { DefaultTheme } from "styled-components/native";
import { CoachOverlayService } from "@services/CoachOverlayService";

const CustomTooltip = ({ labels }: TooltipProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const { deviceWidth: screenWidth, isFold } = useIsFoldable();
  const toastVisibilityTime = useSelector(
    (state: any) => state.appState?.toastVisibilityTime
  );
  const {
    currentStep,
    goToNext,
    goToPrev,
    isFirstStep,
    isLastStep,
    currentStepNumber,
    totalStepsNumber,
  } = useCopilot();
  const dispatch = useDispatch();
  const getOverlayConfig = useOverlayConfig();

  const handleAction = () => {
    CoachOverlayService.completeTour();

    if (toastVisibilityTime) {
      dispatch(setCoachOverlayToast(false));
      showToast(
        undefined,
        t("coachOverlay.toast.description.text"),
        ToastTypes.SUCCESS,
        true,
        true,
        undefined,
        undefined,
        true,
        Number(getOverlayConfig?.toastVisibilityTime) || 5000
      );
    }
  };

  const handleOverlayAction = () => {
    handleAction();
  };

  return (
    <TooltipContainer screenWidth={screenWidth} isFold={isFold}>
      <VDBText color={theme["core-05"]} type={typography["Body-R"]}>
        {`${t(currentStep?.text ?? "")}`}
      </VDBText>
      <Spacer size={SpaceType.Basic} />
      <ButtonContainer>
        <OverLayContainer>
          <StepContainer>
            <VDBText color={theme["core-05"]} type={typography["Body-M"]}>
              {`${currentStepNumber}/${totalStepsNumber}`}
            </VDBText>
          </StepContainer>
        </OverLayContainer>

        <OverLayContainer>
          {!isFirstStep && totalStepsNumber > 1 && (
            <TouchableOpacity onPress={() => goToPrev()}>
              <TextContainer>
                <VDBText
                  color={theme["link-static"]}
                  type={typography["Body-M"]}
                >
                  {labels.previous || "Previous"}
                </VDBText>
              </TextContainer>
            </TouchableOpacity>
          )}
          <Spacer size={SpaceType.Basic} />
          {!isLastStep && totalStepsNumber > 1 && (
            <TouchableOpacity
              onPress={() => (isLastStep ? handleOverlayAction() : goToNext())}
            >
              <TextContainer>
                <VDBText
                  color={theme["link-static"]}
                  type={typography["Body-M"]}
                >
                  {labels.next || "Next"}
                </VDBText>
              </TextContainer>
            </TouchableOpacity>
          )}
          {(isLastStep || totalStepsNumber === 1) && (
            <>
              <TouchableOpacity
                onPress={() =>
                  isLastStep || totalStepsNumber === 1
                    ? handleOverlayAction()
                    : goToNext()
                }
              >
                <TextContainer>
                  <VDBText
                    color={theme["link-static"]}
                    type={typography["Body-M"]}
                  >
                    {labels.finish}
                  </VDBText>
                </TextContainer>
              </TouchableOpacity>
            </>
          )}
        </OverLayContainer>
      </ButtonContainer>
    </TooltipContainer>
  );
};

const TooltipContainer = styled.View<{ isFold: boolean; screenWidth: number }>`
  ${({
    theme,
    screenWidth,
    isFold,
  }: {
    theme: DefaultTheme;
    screenWidth: number;
    isFold: boolean;
  }) => {
    if (theme.isLargeScreen || theme.isTablet) {
      return css`
        max-width: ${screenWidth * 0.4}px;
      `;
    }

    if (isFold) {
      return css`
        max-width: ${screenWidth}px;
      `;
    }

    return "";
  }}
  padding-bottom: ${SpaceType.Double}px;
  padding-horizontal: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
`;

const OverLayContainer = styled.View`
  flex-direction: row;
`;

const ButtonContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;
const TextContainer = styled.View`
  padding: ${SpaceType.Basic}px;
`;

const StepContainer = styled.View`
  padding-top: ${SpaceType.Basic}px;
  padding-bottom: ${SpaceType.Basic}px;
  padding-right: ${SpaceType.Basic}px;
`;

export default CustomTooltip;
